import React, { useCallback } from 'react';
import Field from '../components/Field';
import StatusBadge from '../components/StatusBadge';
import MatrixGrid from '../components/MatrixGrid';
import VerifyTools from '../components/VerifyTools';
import {
  STATUS,
  makeSiteElement,
  SITE_ELEMENT_TYPES,
} from '../state/model';
import { cellKey, emptyCell, makeRow, makeColumn, AREA_CLASS } from '../state/matrix';
import { buildExistingDescription } from '../state/describe';

const EXIST_VERIFY_KEYS = ['builtArea', 'units', 'rooms', 'volume', 'storiesAbove', 'storiesBelow', 'height', 'footprint'];

// מצב קיים — built from parcel-specific sources (תיק בניין as-built + permit
// timeline). Regional plans stay in the Plan Stack on the property tab.
export default function ExistingStateTab({ project, updateProject }) {
  const es = project.existingState;

  const setSummary = useCallback((key, partial) => {
    updateProject((p) => {
      p.existingState.summary[key] = { ...p.existingState.summary[key], ...partial };
    }, `עודכן נתון מצב קיים: ${key}`);
  }, [updateProject]);

  const setDescription = useCallback((partial) => {
    updateProject((p) => { p.existingState.description = { ...p.existingState.description, ...partial }; });
  }, [updateProject]);

  const regenerateDescription = useCallback(() => {
    updateProject((p) => {
      p.existingState.description = {
        value: buildExistingDescription(p), status: STATUS.AUTO, source: 'נוצר אוטומטית', confidence: null, note: null,
      };
    }, 'נוצרה מחדש פסקת תיאור מצב קיים');
  }, [updateProject]);

  // ---- matrix handlers ----
  const setCell = useCallback((rowId, colId, sqm) => {
    updateProject((p) => {
      const m = p.existingState.matrix;
      const k = cellKey(rowId, colId);
      m.cells[k] = { ...(m.cells[k] || emptyCell()), sqm, status: sqm == null ? STATUS.MISSING : STATUS.MANUAL };
    }, 'עודכן תא במטריצת מצב קיים');
  }, [updateProject]);

  const addRow = useCallback((label) => {
    updateProject((p) => { p.existingState.matrix.rows.push(makeRow({ label })); }, 'נוסף מפלס למטריצה');
  }, [updateProject]);

  const addColumn = useCallback((label) => {
    updateProject((p) => {
      const key = `c${p.existingState.matrix.columns.length}`;
      p.existingState.matrix.columns.push(makeColumn(key, label, AREA_CLASS.UNCOUNTED));
    }, 'נוסף רכיב למטריצה');
  }, [updateProject]);

  const setColClass = useCallback((colId, classification) => {
    updateProject((p) => {
      const col = p.existingState.matrix.columns.find((c) => c.id === colId);
      if (col) col.classification = classification;
    }, 'עודכן סיווג שטח');
  }, [updateProject]);

  // ---- site elements ----
  const addSiteElement = useCallback(() => {
    updateProject((p) => { p.existingState.siteElements.push(makeSiteElement()); }, 'נוסף רכיב מצב קיים');
  }, [updateProject]);

  const updateSiteElement = useCallback((id, patch) => {
    updateProject((p) => {
      const el = p.existingState.siteElements.find((e) => e.id === id);
      if (el) Object.assign(el, patch);
    });
  }, [updateProject]);

  const removeSiteElement = useCallback((id) => {
    updateProject((p) => {
      p.existingState.siteElements = p.existingState.siteElements.filter((e) => e.id !== id);
    }, 'הוסר רכיב מצב קיים');
  }, [updateProject]);

  const fromBuildingFile = es.builtAsRaw && es.builtAsRaw.building_file_evidence;

  const evidenceUrl = es.summary.builtArea?.sourceUrl || null;

  const approveAllExist = () => updateProject((p) => {
    for (const k of EXIST_VERIFY_KEYS) {
      if (p.existingState.summary[k]?.status === STATUS.AUTO) {
        p.existingState.summary[k] = { ...p.existingState.summary[k], status: STATUS.APPROVED };
      }
    }
  }, 'אושרו כל החילוצים — מצב קיים');

  return (
    <div className="rd-existing-tab">
      {fromBuildingFile && (
        <div className="rd-card-warn" data-rd-anchor="card-buildingfile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '12px 18px', borderRadius: 12 }}>
          <span>הנתונים חולצו ממסמך סרוק — פרוטוקול ביקורת בגמר הבנייה. אין מדידת פנים מאושרת ולא נרשם טופס 4.</span>
          {evidenceUrl && (
            <a href={evidenceUrl} target="_blank" rel="noreferrer" className="rd-mini-btn" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
              פתח את המסמך ↗
            </a>
          )}
        </div>
      )}

      {/* ---- As-built summary (parcel-specific) ---------------------------- */}
      <section className="rd-card">
        <div className="rd-card-head-row" style={{ marginBottom: 16 }}>
          <div>
            <h3 className="rd-card-title">מצב בנוי קיים — סיכום</h3>
            <p className="rd-card-sub" style={{ margin: '3px 0 0' }}>נתונים ספציפיים לנכס, מתוך תיק הבניין וההיתרים (לא מתכניות אזוריות).</p>
          </div>
          <VerifyTools obj={es.summary} keys={EXIST_VERIFY_KEYS} onApproveAll={approveAllExist} />
        </div>
        {!es.builtAsRaw && (
          <p className="rd-card-warn">תיק בניין/מצב בנוי לא נמשך עדיין. בחרו כתובת בלשונית פרטי הנכס.</p>
        )}
        <div className="rd-field-group">
          <div className="rd-field-group-title">שטחים ויחידות</div>
          <div className="rd-field-grid">
            <Field label='שטח בנוי (מ"ר)' field={es.summary.builtArea} anchor="field-builtArea" type="number" onChange={(p) => setSummary('builtArea', p)} />
            <Field label='יח"ד' field={es.summary.units} anchor="field-units" type="number" onChange={(p) => setSummary('units', p)} />
            <Field label="חדרים" field={es.summary.rooms} type="number" onChange={(p) => setSummary('rooms', p)} />
            <Field label='נפח (מ"ק)' field={es.summary.volume} type="number" onChange={(p) => setSummary('volume', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">מבנה ומידות</div>
          <div className="rd-field-grid">
            <Field label="קומות מעל הכניסה הקובעת" field={es.summary.storiesAbove} anchor="field-storiesAbove" type="number" onChange={(p) => setSummary('storiesAbove', p)} />
            <Field label="קומות מתחת" field={es.summary.storiesBelow} type="number" onChange={(p) => setSummary('storiesBelow', p)} />
            <Field label="גובה (מ')" field={es.summary.height} type="number" onChange={(p) => setSummary('height', p)} />
            <Field label='תכסית קיימת (מ"ר)' field={es.summary.footprint} type="number" onChange={(p) => setSummary('footprint', p)} />
          </div>
        </div>
      </section>

      {/* ---- Existing-state matrix ----------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">מטריצת מצב קיים</h3>
        <p className="rd-card-sub">כל תא הוא אובייקט שטח (שטח · סיווג · סטטוס). מלא לפי קומות ורכיבים. הסיווג (עיקרי/שירות) מזין את מנוע הזכויות.</p>
        <MatrixGrid
          matrix={es.matrix}
          onCellChange={setCell}
          onAddRow={addRow}
          onAddColumn={addColumn}
          onColClassChange={setColClass}
        />
      </section>

      {/* ---- Permit timeline ----------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">היררכיית היתרים</h3>
        <p className="rd-card-sub">
          {project.permitTimeline.length} היתרים/בקשות ששויכו למבנה זה
          {project.permitsMeta?.filtered_out > 0 && ` (סוננו ${project.permitsMeta.filtered_out} של מבנים שכנים)`}.
        </p>
        {project.permitTimeline.length === 0 ? (
          <div className="rd-empty">
            {project.permitsMeta?.filtered_out > 0
              ? `לא נמצאו במאגר היתרים המשויכים למבנה זה — ${project.permitsMeta.filtered_out} ההיתרים שאותרו בסביבה שייכים למבנים אחרים. ציר הזמן ההיסטורי זמין דרך תיק הבניין.`
              : 'לא נמשכו היתרים. משוך נתונים בלשונית פרטי הנכס.'}
          </div>
        ) : (
          <ul className="rd-timeline">
            {project.permitTimeline.map((pm) => (
              <li key={pm.id} className="rd-timeline-item">
                <span className="rd-timeline-year">{pm.year || '—'}</span>
                <div className="rd-timeline-body">
                  <div className="rd-timeline-action">{pm.action || pm.document || 'היתר'}</div>
                  <div className="rd-timeline-meta">
                    {pm.requestNumber && <span>בקשה {pm.requestNumber}</span>}
                    {!pm.requestNumber && pm.document && (
                      pm.url
                        ? <a href={pm.url} target="_blank" rel="noreferrer" className="rd-timeline-link">מקור: {pm.document} ↗</a>
                        : <span>מקור: {pm.document}</span>
                    )}
                    {pm.forms?.length > 0 && <span> · {summarizeForms(pm.forms)}</span>}
                  </div>
                </div>
                <StatusBadge status={pm.statusValue || STATUS.AUTO} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Site elements ------------------------------------------------- */}
      <section className="rd-card">
        <div className="rd-card-head-row">
          <h3 className="rd-card-title">רכיבי מגרש</h3>
          <button type="button" className="rd-mini-btn" onClick={addSiteElement}>+ רכיב</button>
        </div>
        <p className="rd-card-sub">עצים, חניות, גדרות, זיקות הנאה, הפקעות, קווי בניין, שימושים קיימים.</p>
        {es.siteElements.length === 0 ? (
          <div className="rd-empty">לא הוזנו רכיבי מגרש.</div>
        ) : (
          <table className="rd-table">
            <thead>
              <tr><th>סוג</th><th>תיאור</th><th>סטטוס</th><th></th></tr>
            </thead>
            <tbody>
              {es.siteElements.map((el) => (
                <tr key={el.id}>
                  <td>
                    <select className="rd-input rd-input-sm" value={el.type} onChange={(e) => updateSiteElement(el.id, { type: e.target.value })}>
                      {SITE_ELEMENT_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="rd-input rd-input-sm" value={el.description} placeholder="תיאור / כמות" onChange={(e) => updateSiteElement(el.id, { description: e.target.value })} />
                  </td>
                  <td><StatusBadge status={el.status} /></td>
                  <td><button type="button" className="rd-remove-btn" onClick={() => removeSiteElement(el.id)} title="הסר">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- Description paragraph ----------------------------------------- */}
      <section className="rd-card">
        <div className="rd-card-head-row">
          <h3 className="rd-card-title">פסקת תיאור מצב קיים</h3>
          <button type="button" className="rd-mini-btn" onClick={regenerateDescription}>צור מחדש</button>
        </div>
        <Field label="" field={es.description} type="textarea" onChange={setDescription} />
      </section>
    </div>
  );
}

function summarizeForms(forms = []) {
  const bits = [];
  for (const f of forms) {
    const p = [];
    if (f.form_type) p.push(f.form_type);
    if (f.built_area_m2) p.push(`${Math.round(Number(f.built_area_m2))} מ"ר`);
    if (f.units_count) p.push(`${f.units_count} יח"ד`);
    if (p.length) bits.push(p.join(' '));
  }
  return bits.join(' · ');
}
